import { useCallback, useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
    AGENT_RUN_UPDATED_SUBJECT,
    AgentRunUpdatedEvent,
} from "@common/types/agent-run-nats";
import type { OrganizationNotificationSignal } from "@common/types/organization-nats";
import {
    TASK_BULK_SAVED_SUBJECT,
    TASK_SAVED_SUBJECT,
    TaskBulkSavedEvent,
    TaskSavedEvent,
} from "@common/types/task-nats";
import { agentRunsQueryKeys } from "@api/agentRun.api";
import { useNatsSubscription } from "@hooks/useNatsSubscription";
import { ScopeContext, useScope } from "@state/scopeContext.lib";

/**
 * Keeps the Agent panel's run list current: `agent-run.updated` is an
 * invalidation published after every visible run write, and task saves move
 * the close task counts without touching the run row. Own writes are not
 * skipped: the pause acknowledgement arrives from the worker later. On an
 * organization page the same subjects arrive on the organization's
 * subjects and refresh the organization list and every task timeline.
 */
export function useAgentRunLiveUpdates() {
    const { workspaceId } = useScope();
    const root = useContext(ScopeContext);
    const organizationId =
        root.organizationId && !root.workspaceId ? root.organizationId : null;
    const queryClient = useQueryClient();

    const invalidateList = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: agentRunsQueryKeys.list(workspaceId),
            exact: true,
        });
        queryClient.invalidateQueries({
            queryKey: agentRunsQueryKeys.taskActivityAll(workspaceId),
        });
    }, [queryClient, workspaceId]);

    const invalidateOrganizationList = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: agentRunsQueryKeys.organizationList(organizationId),
            exact: true,
        });
        queryClient.invalidateQueries({
            queryKey: agentRunsQueryKeys.taskActivityAny(),
        });
    }, [queryClient, organizationId]);

    const subjectFor = (name: string) =>
        workspaceId ? `workspaces.${workspaceId}.notifications.${name}` : null;
    const organizationSubjectFor = (name: string) =>
        organizationId
            ? `organizations.${organizationId}.notifications.${name}`
            : null;

    useNatsSubscription<{ data: AgentRunUpdatedEvent }>(
        subjectFor(AGENT_RUN_UPDATED_SUBJECT),
        { onMessage: invalidateList },
    );
    useNatsSubscription<{ data: TaskSavedEvent }>(
        subjectFor(TASK_SAVED_SUBJECT),
        { onMessage: invalidateList },
    );
    useNatsSubscription<{ data: TaskBulkSavedEvent }>(
        subjectFor(TASK_BULK_SAVED_SUBJECT),
        { onMessage: invalidateList },
    );
    // Organization subjects carry a refresh signal, not the event.
    useNatsSubscription<{ data: OrganizationNotificationSignal }>(
        organizationSubjectFor(AGENT_RUN_UPDATED_SUBJECT),
        { onMessage: invalidateOrganizationList },
    );
    useNatsSubscription<{ data: OrganizationNotificationSignal }>(
        organizationSubjectFor(TASK_SAVED_SUBJECT),
        { onMessage: invalidateOrganizationList },
    );
    useNatsSubscription<{ data: OrganizationNotificationSignal }>(
        organizationSubjectFor(TASK_BULK_SAVED_SUBJECT),
        { onMessage: invalidateOrganizationList },
    );
}
